import { useWeb3React } from '@web3-react/core'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { useAppDispatch } from '~/state/hooks'
import { useSpotSwapTokens, useIsDisableTrade } from '~/state/dex/hooks'
import { updateSpotSwapPair } from '~/state/dex/actions'
import { LON, USDT } from '~/constants/addresses'
import { getStakingDashboard, getStakingApyAndScale } from '~/apis'
import DataContainer from '~/components/DataContainer'
import Tooltip from '~/components/Tooltip'
import { formatNumber } from '~/utils/numbers'
import {
  StyledLonMarketContainer,
  StyledStakeDataContainer,
  StyledXLonScaleWrapper,
  StyledButton,
} from './styled'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'

const LonMarket: React.FC = () => {
  const { t } = useTranslation()
  const { chainId } = useWeb3React()
  const dispatch = useAppDispatch()
  const tokens = useSpotSwapTokens(chainId)

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const tokensKeys = Object.keys(tokens)
  const foundUSDTKey = tokensKeys.find(
    (key) => key.toLowerCase() === USDT[chainId]?.toLowerCase(),
  )
  const foundLONKey = tokensKeys.find(
    (key) => key.toLowerCase() === LON[chainId]?.toLowerCase(),
  )
  const inputToken = tokens[foundUSDTKey]
  const outputToken = tokens[foundLONKey]

  const { data: stakingDashboard } = useSWR(
    ['getStakingDashboard', chainId],
    () => getStakingDashboard(chainId),
  )

  const { data: apyAndScale } = useSWR(['getStakingApyAndScale', chainId], () =>
    getStakingApyAndScale(),
  )

  const handleBuyLon = (e) => {
    if (isDisableTrade) {
      e.preventDefault()
      toggleIpBlockedModal()
      return
    }
    if (inputToken && outputToken) {
      dispatch(
        updateSpotSwapPair({
          inputToken,
          outputToken,
        }),
      )
    }
  }

  return (
    <StyledLonMarketContainer>
      <h4 className="title">{t('summary')}</h4>
      <StyledStakeDataContainer>
        <DataContainer
          dataTitle={t('staking_total_lon_staked')}
          description={`${formatNumber(
            stakingDashboard?.totalStakedAmount,
            2,
          )} LON`}
          title={null}
        />
        <DataContainer
          dataTitle={t('staking_total_xlon_amount')}
          middleDataTitle={
            <Tooltip
              className="icon-exclamation"
              placement="topLeft"
              title={t('staking_scale_tips')}
            >
              <StyledXLonScaleWrapper>
                xLON : LON = {formatNumber(stakingDashboard?.scaleIndex, 4)}
              </StyledXLonScaleWrapper>
            </Tooltip>
          }
          description={`${formatNumber(
            stakingDashboard?.totalXlonSupply,
            2,
          )} xLON`}
          title={null}
          className="middle-container"
        ></DataContainer>
        <DataContainer
          dataTitle={t('apy_avg')}
          description={
            isNaN(Number(apyAndScale?.apy))
              ? '~'
              : Number(apyAndScale?.apy).toFixed(2) + '%'
          }
          title={t('apy_avg_tips')}
        >
          <StyledButton
            href="/exchange"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBuyLon}
          >
            {t('staking_buy_lon')}
          </StyledButton>
        </DataContainer>
      </StyledStakeDataContainer>
    </StyledLonMarketContainer>
  )
}

export default LonMarket
